import { Group, Stack, useMantineTheme } from '@mantine/core';
import type {
	BarChartConfig,
	BiMetricConfig,
	PieChartConfig,
	ScatterChartConfig,
} from '@repo/api-codegen';
import { Select, Switch, TextInput } from '@repo/foundations';
import { cloneDeep, keys } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { SelectableProperty } from '../../../components/EntityPageLayout/EntityPropertySidebar';
import { PropertySelector } from '../../../components/Properties/PropertySelector';
import { ChartConfigHelper } from '../../../utils/charts/bi/ChartConfigHelper';
import { toTitleCase } from '../../../utils/charts/utils';
import { getAxesSettings } from './ChartTypeSelector.utils';

interface XAxisSelectorProps {
	chartConfig: BiMetricConfig['chart_config'];
	onUpdate: (config: BiMetricConfig['chart_config']) => void;
}

const updateXAxis = (
	config: BiMetricConfig['chart_config'],
	value: string
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (config.chartType === 'bar' || config.chartType === 'line') {
		const barAndLineConfig = newConfig as BarChartConfig;
		barAndLineConfig.axes.x = [value];
	}

	if (config.chartType === 'scatter') {
		const scatterConfig = newConfig as ScatterChartConfig;
		scatterConfig.axes.x = [value];
	}

	if (config.chartType === 'pie') {
		const pieConfig = newConfig as PieChartConfig;
		pieConfig.axes.x = [value];
	}

	return newConfig;
};

const updateXAxisShowTitle = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.xAxisConfig) {
		newConfig.xAxisConfig = {
			xAxisTimeInterval: 'auto',
		};
	}
	newConfig.xAxisConfig.xAxisShowTitle = value;
	return newConfig;
};

const updateXAxisTitle = (
	config: BiMetricConfig['chart_config'],
	value: string
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.xAxisConfig) {
		newConfig.xAxisConfig = {
			xAxisTimeInterval: 'auto',
		};
	}
	newConfig.xAxisConfig.xAxisCustomTitle = value || null;
	return newConfig;
};

const updateXAxisShowLabel = (
	config: BiMetricConfig['chart_config'],
	value: boolean
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.xAxisConfig) {
		newConfig.xAxisConfig = {
			xAxisTimeInterval: 'auto',
		};
	}
	newConfig.xAxisConfig.xAxisShowTicks = value;
	return newConfig;
};

const updateXAxisLabelRotation = (
	config: BiMetricConfig['chart_config'],
	value: string
): BiMetricConfig['chart_config'] => {
	const newConfig = cloneDeep(config);
	if (!newConfig.xAxisConfig) {
		newConfig.xAxisConfig = {
			xAxisTimeInterval: 'auto',
		};
	}
	newConfig.xAxisConfig.xAxisTickRotation =
		value === 'auto' ? null : (Number(value) as 0 | 45 | 90);
	return newConfig;
};

export const XAxisSection = observer(
	({ chartConfig, onUpdate }: XAxisSelectorProps) => {
		const mantineTheme = useMantineTheme();
		const chartConfigHelper = new ChartConfigHelper(chartConfig, mantineTheme);
		const axesSettings = getAxesSettings(chartConfig);
		const xAxisColumns = axesSettings?.x || [];
		const columns = keys(chartConfig.columnConfigs);

		return (
			<Stack spacing="xs">
				<SelectableProperty label="Columns" dataTestId="columns">
					<Group w="100%" position="right">
						<PropertySelector
							selected={xAxisColumns[0]}
							type="single"
							onChange={(value) => {
								onUpdate(updateXAxis(chartConfig, value as string));
							}}
							value="columns"
							iconType="none"
							options={columns.map((column) => ({
								label: toTitleCase(column),
								value: column,
								icon: 'column',
							}))}
							isViewerUser={false}
						/>
					</Group>
				</SelectableProperty>
				<SelectableProperty
					label="Show axis title"
					dataTestId="show-axis-title"
				>
					<Group w="100%" position="right">
						<Switch
							checked={chartConfigHelper.shouldShowXAxisTitle}
							onChange={(event) => {
								onUpdate(
									updateXAxisShowTitle(chartConfig, event.currentTarget.checked)
								);
							}}
						/>
					</Group>
				</SelectableProperty>
				{chartConfigHelper.shouldShowXAxisTitle && (
					<SelectableProperty label="Axis title" dataTestId="axis-title">
						<Group w="100%" position="right">
							<TextInput
								size="sm"
								value={chartConfig.xAxisConfig?.xAxisCustomTitle || ''}
								onChange={(event) => {
									onUpdate(
										updateXAxisTitle(chartConfig, event.currentTarget.value)
									);
								}}
								placeholder={chartConfigHelper.xAxisTitle}
							/>
						</Group>
					</SelectableProperty>
				)}
				<SelectableProperty
					label="Show axis label"
					dataTestId="show-axis-label"
				>
					<Group w="100%" position="right">
						<Switch
							checked={chartConfigHelper.shouldShowXAxisTicks}
							onChange={(event) => {
								onUpdate(
									updateXAxisShowLabel(chartConfig, event.currentTarget.checked)
								);
							}}
						/>
					</Group>
				</SelectableProperty>
				<SelectableProperty label="Label rotation" dataTestId="label-rotation">
					<Group w="100%" position="right">
						<Select
							value={chartConfigHelper.xAxisLabelRotationValue}
							onChange={(value) => {
								onUpdate(
									updateXAxisLabelRotation(chartConfig, value as string)
								);
							}}
							data={[
								{ value: 'auto', label: 'Auto' },
								{ value: '0', label: '0°' },
								{ value: '45', label: '45°' },
								{ value: '90', label: '90°' },
							]}
						/>
					</Group>
				</SelectableProperty>
			</Stack>
		);
	}
);
